import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { formatDate, getStatusColor } from '../../utils/helpers';
import { ClockIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

var userEmail;
var JSToken;
var accessToken;
if (localStorage.user) {
  userEmail = localStorage.user_email;
  JSToken = JSON.parse(localStorage.user);
  accessToken = JSToken.access_token;
}
const config = {
  headers: { Authorization: `Bearer ${accessToken}` },
};

const TransferHistoryCard = ({ delegateEmail, requestId, deduction }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
         const response = await axios.get(
          `https://dash.bestdiplomats.org/api/method/get_transfer_history?delegate_email=${delegateEmail}&request_id=${requestId}`,
          config
        );
        if (response.status===200) {
          setHistory(response.data.data);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (delegateEmail) {
      fetchHistory();
    }
  }, [delegateEmail]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <Skeleton height={30} width={250} className="mb-4" />
        <Skeleton count={3} height={100} className="mb-3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-red-600">Error loading transfer history: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white">Transfer History</h3>
        <p className="text-teal-100 text-sm">
          {history.length} {history.length === 1 ? 'transfer' : 'transfers'} on record
        </p>
      </div>

      <div className="p-6">
        {history.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No transfer history found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((transfer, index) => (
              <div
                key={transfer.name}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-mono text-gray-500">
                        #{transfer.name}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(transfer.approval_status)}`}>
                        {transfer.approval_status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Requested on {formatDate(transfer.creation, 'MMM DD, YYYY')}
                    </p>
                  </div>
                  {transfer.delta && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Days to Event</p>
                      <p className="text-lg font-bold text-gray-900">{transfer.delta}</p>
                    </div>
                  )}
                </div>

                {/* Transfer Flow */}
                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg mb-3">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">From</p>
                    <p className="font-semibold text-sm text-gray-900">
                      {transfer.source_event_name}
                    </p>
                    {transfer.source_event_date && (
                      <p className="text-xs text-gray-500">
                        {formatDate(transfer.source_event_date, 'MMM DD, YYYY')}
                      </p>
                    )}
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">To</p>
                    <p className="font-semibold text-sm text-gray-900">
                      {transfer.dest_event_name}
                    </p>
                    {transfer.dest_event_date && (
                      <p className="text-xs text-gray-500">
                        {formatDate(transfer.dest_event_date, 'MMM DD, YYYY')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Deduction</p>
                    <p className="font-medium text-gray-900">{deduction || 'N/A'}</p>
                  </div>
                </div>

                {/* Comments */}
                {(transfer.approval_comment || transfer.rejection_comment) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    {transfer.approval_comment && (
                      <div className="mb-2">
                        <p className="text-xs text-green-600 font-medium">Approval:</p>
                        <p className="text-xs text-gray-700">{transfer.approval_comment}</p>
                      </div>
                    )}
                    {transfer.rejection_comment && (
                      <div>
                        <p className="text-xs text-red-600 font-medium">Rejection:</p>
                        <p className="text-xs text-gray-700">{transfer.rejection_comment}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Documents */}
                {(transfer.first_receipt || transfer.second_receipt || transfer.appeal_attachment) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 font-medium mb-2">Documents:</p>
                    <div className="flex gap-2 flex-wrap">
                      {transfer.first_receipt && (
                        <a
                          href={`https://dash.bestdiplomats.org${transfer.first_receipt}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                        >
                          Receipt 1
                        </a>
                      )}
                      {transfer.second_receipt && (
                        <a
                          href={`https://dash.bestdiplomats.org${transfer.second_receipt}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                        >
                          Receipt 2
                        </a>
                      )}
                      {transfer.appeal_attachment && (
                        <a
                          href={`https://dash.bestdiplomats.org${transfer.appeal_attachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200"
                        >
                          Appeal
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferHistoryCard;